import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OverviewTab } from './OverviewTab.jsx';
// Imported for internal use and re-exported so existing tests that import
// CapabilitiesTab from ApplicationDetail.jsx continue to work without modification.
import { CapabilitiesTab } from './CapabilitiesTab.jsx';
export { CapabilitiesTab };
import { AccessTab } from './AccessTab.jsx';
import { ActTab } from './ActTab.jsx';
// Shared helpers live in applicationDetailShared.js to avoid circular imports
// between this file and the tab files. Re-exported here for backward compat.
export { urnShort, accessViewToMatrix } from './applicationDetailShared.js';
import { safeStr } from './applicationDetailShared.js';

const TABS = [
  { k: 'overview',     l: 'Overview' },
  { k: 'capabilities', l: 'Capabilities' },
  { k: 'access',       l: 'Access' },
  { k: 'act',          l: 'Act' },
];

function useApplicationDetail(id) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  useEffect(() => {
    let alive = true;
    setState({ status: 'loading', data: null, error: null });
    fetch(`/api/applications/${encodeURIComponent(id)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => alive && setState({ status: 'ok', data: j, error: null }))
      .catch((e) => alive && setState({ status: 'error', data: null, error: String(e) }));
    return () => { alive = false; };
  }, [id]);
  return state;
}

// JOBE-0150: per-Application activity rollup for the Overview tab's
// Activity strip. Fetches the last-invocation timestamp + 24h / 7d
// counts on Overview mount. The hook stays inactive until `enabled`
// flips true so non-Overview tab opens don't pay the round trip.
//
// Error state is intentionally swallowed — Activity is a soft surface
// that the SPA hides on failure rather than rendering an error banner.
// The OverviewTab gates its render on `lastInvocation != null`, so a
// failed fetch (state.data === null) takes the same quiet path as a
// never-invoked Application.
function useApplicationActivity(id, enabled) {
  const [state, setState] = useState({ status: 'idle', data: null, error: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setState({ status: 'loading', data: null, error: null });
    fetch(`/api/applications/${encodeURIComponent(id)}/activity`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => alive && setState({ status: 'ok', data: j, error: null }))
      .catch((e) => alive && setState({ status: 'error', data: null, error: String(e) }));
    return () => { alive = false; };
  }, [id, enabled]);
  return state;
}

// JOBE-0132: live Cedar projection for the Access tab. Fetches the
// per-agent grant matrix from /api/applications/:id/access on tab
// open. The hook stays inactive until `enabled` flips true so the
// other tabs don't pay the round trip.
function useApplicationAccess(id, enabled) {
  const [state, setState] = useState({ status: 'idle', data: null, error: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setState({ status: 'loading', data: null, error: null });
    fetch(`/api/applications/${encodeURIComponent(id)}/access`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => alive && setState({ status: 'ok', data: j, error: null }))
      .catch((e) => alive && setState({ status: 'error', data: null, error: String(e) }));
    return () => { alive = false; };
  }, [id, enabled]);
  return state;
}

function Toast({ msg, onDismiss }) {
  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(onDismiss, 2400);
    return () => clearTimeout(t);
  }, [msg, onDismiss]);
  if (!msg) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'var(--ink)', color: 'var(--paper)', padding: '10px 16px',
      borderRadius: 2, fontFamily: 'var(--ff-mono)', fontSize: 12, zIndex: 100,
      boxShadow: '3px 3px 0 var(--ink)',
    }}>{msg}</div>
  );
}

export function ApplicationDetail({ id, onOpenChat }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { status, data: app, error } = useApplicationDetail(id);
  const [toast, setToast] = useState(null);
  const dismissToast = useCallback(() => setToast(null), []);
  const modalRef = useRef(null);

  // Focus the modal on mount so our scoped Escape handler actually
  // receives keyboard events (otherwise Escape hits the body / Shell).
  useEffect(() => {
    modalRef.current?.focus();
  }, [id]);

  const tab = TABS.some((t) => t.k === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'overview';

  // JOBE-0132: fetch the live access projection only when the tab is
  // visible. The endpoint runs one Cedar eval per (agent, capability)
  // pair on the operator — cheap, but no reason to pre-warm it for
  // users who never open Access.
  const access = useApplicationAccess(id, tab === 'access' || tab === 'act');

  // JOBE-0150: fetch the activity rollup for Overview's Activity strip.
  // One Postgres aggregate per modal open on Overview; no need to
  // pre-warm for other tabs.
  const activity = useApplicationActivity(id, tab === 'overview');

  const close = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    navigate(`/applications${next.toString() ? `?${next}` : ''}`);
  }, [navigate, searchParams]);

  const setTab = (k) => {
    const next = new URLSearchParams(searchParams);
    if (k === 'overview') next.delete('tab'); else next.set('tab', k);
    setSearchParams(next, { replace: true });
  };

  const capCount = app?.capabilities?.length ?? 0;

  const body = useMemo(() => {
    if (status === 'loading') {
      return <div style={{ padding: 40, fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)' }}>loading…</div>;
    }
    if (status === 'error') {
      return (
        <div style={{ padding: 40, fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)' }}>
          <div style={{ marginBottom: 8 }}>Could not load application <b>{id}</b>.</div>
          <div style={{ fontSize: 11 }}>{error}</div>
        </div>
      );
    }
    if (!app) return null;
    if (tab === 'overview')     return <OverviewTab app={app} activity={activity.data} onToast={setToast} />;
    if (tab === 'capabilities') return <CapabilitiesTab app={app} />;
    if (tab === 'access')       return <AccessTab access={access} />;
    if (tab === 'act')          return <ActTab app={app} access={access} onOpenChat={onOpenChat} onClose={close} />;
    return null;
  }, [status, app, tab, error, id, onOpenChat, close, access, activity]); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <div className="applications-route">
      <div
        className="scrim"
        onClick={close}
        onKeyDown={(e) => {
          // Scope Escape to the modal tree so the Shell's global palette
          // bindings don't also react to it.
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            close();
          }
        }}
      >
        <div className="modal" ref={modalRef} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="app-detail-crumb" tabIndex={-1}>
          <div className="mhead">
            <div className="crumb" id="app-detail-crumb">
              Applications / <b>{safeStr(app?.name) || id}</b>
            </div>
            <button className="x" onClick={close} aria-label="Close">✕</button>
          </div>
          <div className="mtabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.k}
                role="tab"
                aria-selected={tab === t.k}
                className="mtab"
                onClick={() => setTab(t.k)}
              >
                {t.l}
                {t.k === 'capabilities' && capCount > 0 && <span className="n">{capCount}</span>}
              </button>
            ))}
          </div>
          <div className="mbody">{body}</div>
        </div>
      </div>
      <Toast msg={toast} onDismiss={dismissToast} />
    </div>,
    document.body,
  );
}
